<script setup>
import Breadcrumb from "primevue/breadcrumb";
import {computed, ref, watch} from "vue";
import {useCustomerStore} from "@/stores/customer.js";
import {useFacilityStore} from "@/stores/facility.js";
import {useWorksStore} from "@/stores/work.js";
import {useContractsStore} from "@/stores/contract.js";
import {useRoute} from "vue-router";
import {useEmployeeStore} from "@/stores/employee.js";
import { useWindowSize } from "@vueuse/core";


const home = ref({
  icon: 'pi pi-home',
  route: '/',
  isHome: true,
})

const customerStore = useCustomerStore();
const facilityStore = useFacilityStore();
const workStore = useWorksStore()
const contractStore = useContractsStore()
const employeeStore = useEmployeeStore()

const route = useRoute();

watch(
    () => route.params.id,
    (id) => {
      if (route.name === "customer" && id) customerStore.loadCustomer(id);
      if (route.name === "facility" && id) facilityStore.loadFacility(id);
      if (route.name === "work" && id) workStore.loadWork(id);
      if (route.name === "contract" && id) contractStore.loadContract(id);
      if (route.name === "employee" && id) employeeStore.loadEmployee(id);
    },
    { immediate: true }
);

const items = computed(() => {
  if (route.name === "customers") return [{ label: "Клиенты", route: "/customers" }];
  if (route.name === "customer") {
    const label =
        customerStore.customer?.short_name ||
        customerStore.customer?.name ||
        `Клиент #${route.params.id}`;
    return [{ label: "Клиенты", route: "/customers" }, { label }];
  }
  if (route.name === "facilities") return [{ label: "Объекты", route: "/facilities" }];
  if (route.name === "facility") {
    const label =
        facilityStore.facility?.short_name ||
        facilityStore.facility?.name ||
        `Объект #${route.params.id}`;
    return [{ label: "Объекты", route: "/facilities" }, { label }];
  }
  if (route.name === "works") return [{ label: "Работы", route: "/works" }];
  if (route.name === "work") {
    const label =
        workStore.work?.name ||
        workStore.work?.name ||
        `Работа #${route.params.id}`;
    return [{ label: "Работы", route: "/works" }, { label }];
  }
  if (route.name === "contracts") return [{ label: "Договоры", route: "/contracts" }];
  if (route.name === "contract") {
    const label =
        contractStore.contract?.number ||
        contractStore.contract?.number ||
        `Договор #${route.params.id}`;
    return [{ label: "Договоры", route: "/contracts" }, { label }];
  }
  if (route.name === "employees") return [{ label: "Сотрудники", route: "/employees" }];
  if (route.name === "employee") {
    const label =
        employeeStore.employee?.last_name ||
        employeeStore.employee?.first_name ||
        `Сотрудник #${route.params.id}`;
    return [{ label: "Сотрудники", route: "/employees" }, { label }];
  }
  if (route.name === "contacts") return [{ label: "Контакты", route: "/contacts" }];
  if (route.name === "departments") return [{ label: "Отделы", route: "/departments" }];
  if (route.name === "positions") return [{ label: "Должности", route: "/positions" }];

  return [{ label: "Главная", route: "/" }];
});

function truncate(text, max = 80) {
  const s = text == null ? '' : String(text);
  return s.length > max ? s.slice(0, max) + '...' : s;
}

// Динамическая длина усечения для последнего элемента, зависит от ширины экрана
const { width } = useWindowSize();
const maxLabelLen = computed(() => {
  const w = width.value || 0;
  if (w < 360) return 18;
  if (w < 480) return 22;
  if (w < 640) return 26;
  if (w < 820) return 34;    // портретный планшет
  if (w < 1024) return 50;   // граница между md и lg
  if (w < 1280) return 60;
  return 80;
});

// Строим безопасную модель для рендера: помечаем последний элемент и сокращаем его
const viewItems = computed(() => {
  const base = (items.value || []).map(i => ({ ...i, isLast: false, labelShort: i.label }));
  if (base.length > 0) {
    const lastIdx = base.length - 1;
    const original = base[lastIdx]?.label ?? '';
    const shortened = truncate(original, maxLabelLen.value);
    base[lastIdx].isLast = true;
    base[lastIdx].labelShort = shortened;
  }
  return base;
});

</script>

<template>
  <Breadcrumb :home="home" :model="viewItems" class="font-light">
    <template #item="{ item, props }">
      <template v-if="item.isLast">
        <span class="flex items-center gap-1 cursor-default select-none">
          <span v-if="item.icon" :class="item.icon" />
          {{ item.labelShort }}
        </span>
      </template>
      <template v-else>
        <router-link
            v-if="item.route"
            v-slot="{ href, navigate }"
            :to="item.route"
            custom
        >
          <a
              :href="href"
              v-bind="props.action"
              @click="navigate"
          >
            <span v-if="item.icon" :class="item.icon" />
            {{ item.label }}
          </a>
        </router-link>
        <a
            v-else
            :href="item.url"
            :target="item.target"
            v-bind="props.action"
        >
          <span v-if="item.icon" :class="item.icon" />
          {{ item.label }}
        </a>
      </template>
    </template>
  </Breadcrumb>
</template>

<style scoped>
</style>